import type { HttpErrorResponse } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';

import type { StoreConfig } from '@ngrx/store';
import { createSelector } from '@ngrx/store';
import type {
  SeatKind,
  TellentCurrentUser,
  TellentCurrentUserUpdate,
  TellentMember,
  TellentPasswordChange,
  TellentPasswordChangeResponse,
} from '@tellent/types';
import { TELLENT_CURRENT_USER } from '@tellent/types';

import type { SingleEntityState } from '@recruitee/ngrx';
import { createSingleEntityStore, memoizeSelector, ngrxEvent, pipeState } from '@recruitee/ngrx';

import { EntityStoreKey } from '../types';

export const TellentCurrentUserConfigToken = new InjectionToken('TellentCurrentUserConfigToken', {
  providedIn: 'root',
  factory: (): StoreConfig<SingleEntityState<TellentCurrentUser>> => ({
    initialState: {
      loaded: true,
      failed: false,
      pending: false,
      error: false,
      data: inject(TELLENT_CURRENT_USER),
    },
  }),
});

const { actions, selectors, reducer, namespace } = createSingleEntityStore<TellentCurrentUser>()(
  EntityStoreKey.TellentCurrentUser,
  {
    customActions: adapter => ({
      updatePassword: (state, payload: TellentPasswordChange) => adapter.markAsPending(state),
      updatePasswordSuccess: (state, payload: TellentPasswordChangeResponse) =>
        adapter.markAsLoaded(state),
      updatePasswordFail: ngrxEvent<HttpErrorResponse>(),

      updateCurrentUserData: (state, payload: Partial<TellentCurrentUserUpdate>) =>
        adapter.markAsPending(state),
      updateCurrentUserDataSuccess: (state, payload: TellentCurrentUser) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),

      updateCurrentUserDataFail: ngrxEvent<HttpErrorResponse>(),

      leaveCompany: (state, payload: { orgGuid: string }) => adapter.markAsPending(state),
      leaveCompanySuccess: (state, payload: TellentCurrentUser) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),
      leaveCompanyFail: ngrxEvent<HttpErrorResponse>(),

      deleteAccount: (state, payload: { code: string }) => adapter.markAsPending(state),
      deleteAccountSuccess: (state, payload: { success: boolean }) => adapter.markAsLoaded(state),
      deleteAccountFail: ngrxEvent<HttpErrorResponse>(),
    }),
  },
);

/**
 * @deprecated use "currentOrganizationMembershipSelectorSafe" instead
 */
const currentOrganizationMembershipSelector = createSelector(selectors.data, findCurrentMembership);

const currentOrganizationMembershipSelectorSafe = createSelector(
  selectors.data,
  findCurrentMembershipSafe,
);

const seatByKindSelector = memoizeSelector((seatKind: SeatKind) =>
  createSelector(currentOrganizationMembershipSelector, function (membership: TellentMember) {
    return membership?.seats.find(seat => seat.app.kind === seatKind);
  }),
);

const isOrgOwner = createSelector(currentOrganizationMembershipSelector, member => member?.owner);
const isAuthorizedWith = createSelector(selectors.data, (user?: TellentCurrentUser) =>
  user ? user.isAuthorizedWith : null,
);

const TellentCurrentUserSelectors = {
  ...selectors,
  currentOrganizationMembershipSelectorSafe,
  currentOrganizationMembershipSelector,
  seatByKindSelector,
  isOrgOwner,
  isAuthorizedWith,
};

export function findCurrentMembershipSafe(user: TellentCurrentUser | null) {
  return user?.members?.find(member => member.orgGuid === user.currentOrgGuid) ?? null;
}

/**
 * @deprecated use "findCurrentMembershipSafe" instead
 */
export function findCurrentMembership(user: TellentCurrentUser): TellentMember {
  return user.members.find(member => member.orgGuid === user.currentOrgGuid)!;
}

export function fallbackCurrentMembership(user: TellentCurrentUser): TellentMember {
  return user.members.find(member => member.userGuid === user.guid)!;
}

export function isOrgGuidMatchingUserOrg(companyGuid: string, user: TellentCurrentUser) {
  if (!companyGuid) return false;
  return user.members.some(member => member.orgGuid === companyGuid);
}

export {
  actions as TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
  reducer as TellentCurrentUserReducer,
  namespace as TellentCurrentUserNamespace,
};
